import * as Yup from "yup";
import { GATEWAY_TYPE, integrationType } from "../Gateways/constants";
import { newIntegrationType } from "../Gateways/constants";
import {
  SERVICES,
  serviceGatewayType,
  availableServiceGatewayType,
  serviceListType,
  servicesType,
  CUSTOM_FORM_SERVICES_LIST,
  SERVICE_OPTION_LABELS,
  chatSupportDisplayConditionFieldsConfig,
  chatSupportProviderType,
} from "./constants";
import dayjs from "dayjs";
import {
  arrToOptionsRaw,
  cleanObjFromEmptyVal,
  compareArraysWithSort,
} from "../../system/helpers/helpfulFunctions";
import { conditionType } from "./components/restriction/constants";
import {
  buildDisplayCondition,
  parseDisplayCondition,
  parseMultipleValue,
} from "../../system/helpers/displayConditionHelper";
import { displayConditionRuleInitValues } from "../../system/constants";

const getServiceByAlias = (alias: string) =>
  Object.values(SERVICES).find((item) => item.alias === alias);

export const isServiceSupported = (alias: string): boolean =>
  getServiceByAlias(alias) !== undefined;

export const getServiceName = (alias: string): string =>
  getServiceByAlias(alias).name;

export const getServiceType = (alias: string): string =>
  getServiceByAlias(alias).type;

export const getServiceOptionLabel = (option: string): string =>
  SERVICE_OPTION_LABELS[option] || option;

export const prepareNewService = (alias: string): newIntegrationType => {
  return {
    id: alias,
    active: false,
    alias,
    name: getServiceName(alias),
    settings: {},
    type: getServiceType(alias),
    options: [],
  };
};

export const getServices = (serviceList: serviceListType): servicesType => {
  return serviceList
    ? Object.entries(serviceList)
        .filter(([key]) => isServiceSupported(key))
        .map(([key, value]) =>
          !value
            ? prepareNewService(key)
            : { ...value, name: getServiceName(key) }
        )
    : [];
};

export const getAvailableServiceGateways = (
  gateways: integrationType[],
  serviceGateways: serviceGatewayType[]
): availableServiceGatewayType[] => {
  const availableGateways = gateways.filter(
    (gateway) =>
      !serviceGateways.some(
        (serviceGateway) => serviceGateway.id === gateway.id
      )
  );

  const availableServiceGateways = availableGateways.map((gateway) => {
    if (
      gateway.type === GATEWAY_TYPE.SMS ||
      gateway.type === GATEWAY_TYPE.TELEGRAM
    ) {
      return {
        active: false,
        id: gateway.id,
        name: gateway.name,
        phone_code: [],
      };
    }

    return {
      active: false,
      id: gateway.id,
      name: gateway.name,
    };
  });

  return availableServiceGateways;
};

export const getNewServiceInitialValues = (data: newIntegrationType) => {
  const initialValues = {
    active: data?.active,
    alias: data?.alias,
    name: getServiceName(data?.alias),
    settings: getServiceSettings(data?.alias, data?.settings),
    type: data?.type,
  };

  return initialValues;
};

export const getIntegrationInitialValues = (data: integrationType) => {
  const initialValues = {
    id: data?.id,
    brand_id: data?.brand_id,
    alias: data?.alias,
    name: getServiceName(data?.alias),
    active: data?.active,
    type: data?.type,
    created_at: data?.created_at,
    updated_at: data?.updated_at,
    options: data?.options,
    settings: getServiceSettings(data?.alias, data?.settings),
  };

  return initialValues;
};

export const getServiceSettings = (alias, settings: any = {}) => {
  if (alias === SERVICES.NOTIFICATION_KYC.alias) {
    return {
      ...settings,
      email: {
        ...settings?.email,
        active: settings?.email?.active || true,
      },
    };
  }

  if (alias === SERVICES.BTAG_MAPPING.alias) {
    return settings?.map
      ? {
          map: Object.entries(settings?.map)?.map(([key, value], index) => ({
            passcode: key,
            btag: value,
            id: index,
          })),
        }
      : { map: [{ passcode: "", btag: "", id: 0 }] };
  }

  if (alias === SERVICES.CHAT_SUPPORT.alias) {
    const res = { ...settings };

    Object.keys(res).forEach((key) => {
      res[key] = { ...settings[key] };

      if (res[key]?.display_condition) {
        res[key].display_condition = {
          allow: parseDisplayCondition(res[key]?.display_condition).allow,
          deny: parseDisplayCondition(res[key]?.display_condition).deny,
        };
      }
    });

    return res;
  }

  if (alias === SERVICES.SHAREDLUCK.alias) {
    return {
      ...settings,
      games: parseMultipleValue(settings?.games, ""),
      games_synced: parseMultipleValue(settings?.games_synced, ""),
    };
  }

  if (alias === SERVICES.CAPTCHA.alias) {
    return {
      ...settings,
      key: { ...settings.key },
      prosopo: { ...settings.prosopo },
    };
  }

  return { ...settings };
};

export const getPendingSharedLuckGames = (settings: any = {}): string[] => {
  const games: string[] = settings?.games ?? [];
  const gamesSynced: string[] = settings?.games_synced ?? [];

  const pending = new Set([
    ...games.filter((game) => !gamesSynced.includes(game)),
    ...gamesSynced.filter((game) => !games.includes(game)),
  ]);

  return Array.from(pending);
};

export const prepareServiceSettings = (values) => {
  const { alias, settings, type, name } = values;
  if (alias === SERVICES.NOTIFICATION_KYC.alias) {
    return {
      ...settings,
      email: {
        ...settings?.email,
        active: settings?.email?.active || true,
      },
      type,
      name,
      alias,
    };
  }

  if (
    alias === SERVICES.PHONE_VERIFICATION.alias ||
    alias === SERVICES.TWO_FACTOR_AUTHENTICATION.alias ||
    alias === SERVICES.BLACKBOX.alias
  ) {
    return {
      ...settings,
      type,
      name,
      alias,
    };
  }

  if (alias === SERVICES.BTAG_MAPPING.alias) {
    return settings?.map
      ? {
          map: settings?.map.reduce(
            (acc, { btag, passcode }) =>
              passcode ? { ...acc, [passcode]: btag } : acc,
            {}
          ),
        }
      : { map: [] };
  }

  if (alias === SERVICES.LICENCE.alias) {
    Object.keys(settings).forEach((key) => {
      if (settings[key]?.api_key && settings[key]?.domain_types?.length > 0) {
        settings[key].active = settings[key].active || false;
      }
    });
  }

  if (alias === SERVICES.CHAT_SUPPORT.alias) {
    const res = { ...settings };

    Object.keys(res).forEach((key) => {
      res[key] = { ...settings[key] };

      if (res[key]?.display_condition) {
        res[key].display_condition = buildDisplayCondition(
          {
            allow:
              res[key]?.display_condition?.allow ||
              displayConditionRuleInitValues,
            deny:
              res[key]?.display_condition?.deny ||
              displayConditionRuleInitValues,
          },
          chatSupportDisplayConditionFieldsConfig
        );
      }
    });

    return res;
  }

  if (alias === SERVICES.SHAREDLUCK.alias) {
    const { games_synced, ...rest } = settings || {};

    return rest;
  }

  if (alias === SERVICES.CAPTCHA.alias) {
    const { prosopo = {}, key = {}, project_id, ...rest } = settings || {};
    const payload = {
      key: cleanObjFromEmptyVal(key),
      prosopo: cleanObjFromEmptyVal(prosopo),
      ...(project_id && { project_id }),
      ...rest,
    };

    return { ...payload };
  }

  return settings;
};

export const getTableColumnDateSorter = (field) => (a, b) =>
  dayjs(a[field])?.valueOf() - dayjs(b[field])?.valueOf();

export const formatTimestamp = (timestamp: string, timestampFormat) =>
  dayjs(timestamp).format(timestampFormat);

export const normalizeSettings = (settings: any) => {
  if (!settings) return "{}";

  if (typeof settings === "string") {
    try {
      return JSON.stringify(JSON.parse(settings), null, 4);
    } catch {
      return settings;
    }
  }

  return JSON.stringify(settings, null, 4);
};

export const checkIsServiceCustomForm = (alias: string): boolean => {
  return CUSTOM_FORM_SERVICES_LIST.includes(alias);
};

export const checkKycConditionSets = (values: any, currentIndex: number) => {
  if (values?.length === 0 || currentIndex === 0) {
    return true;
  }

  const currentAntifraudArr = values?.[currentIndex]?.antifraud_status;
  const currentKycArr = values?.[currentIndex]?.kyc_status;

  if (!currentAntifraudArr?.length && !currentKycArr?.length) {
    return true;
  }

  for (let i = currentIndex - 1; i >= 0; i--) {
    const equalAntifraud = compareArraysWithSort(
      currentAntifraudArr,
      values?.[i]?.antifraud_status
    );
    const equalKyc = compareArraysWithSort(
      currentKycArr,
      values?.[i]?.kyc_status
    );

    if (equalAntifraud && equalKyc) {
      return false;
    }
  }

  return true;
};

export const checkConditionSets = (
  values: conditionType[],
  currentIndex: number
) => {
  if (values?.length === 0 || currentIndex === 0) {
    return true;
  }

  const currentAntifraudArr =
    values?.[currentIndex]?.conditions?.antifraud_status?.value || [];
  const currentKycArr =
    values?.[currentIndex]?.conditions?.kyc_status?.value || [];
  const currentBtagArr = values?.[currentIndex]?.conditions?.btag?.value || [];
  const currentTagArr = values?.[currentIndex]?.conditions?.tag?.value || [];
  const currentBonusCodeArr =
    values?.[currentIndex]?.conditions?.bonus_code?.value || [];

  if (
    !currentAntifraudArr?.length &&
    !currentKycArr?.length &&
    !currentBtagArr?.length &&
    !currentTagArr?.length &&
    !currentBonusCodeArr?.length
  ) {
    return true;
  }

  const sortById = (a: string, b: string) => Number(a) - Number(b);

  for (let i = currentIndex - 1; i >= 0; i--) {
    const equalAntifraud = compareArraysWithSort(
      currentAntifraudArr,
      values?.[i]?.conditions?.antifraud_status?.value || []
    );
    const equalKyc = compareArraysWithSort(
      currentKycArr,
      values?.[i]?.conditions?.kyc_status?.value || []
    );

    const equalBtag = compareArraysWithSort(
      currentBtagArr,
      values?.[i]?.conditions?.btag?.value || []
    );

    const equalBonusCodes = compareArraysWithSort(
      currentBonusCodeArr,
      values?.[i]?.conditions?.bonus_code?.value || []
    );

    const otherTagArr = values?.[i]?.conditions?.tag?.value || [];
    const currentTagSorted = [...currentTagArr].sort(sortById);
    const otherTagSorted = [...otherTagArr].sort(sortById);
    const equalTag =
      currentTagArr.length === otherTagArr.length &&
      currentTagSorted.every((id, idx) => id === otherTagSorted[idx]);

    if (
      equalAntifraud &&
      equalKyc &&
      equalBtag &&
      equalTag &&
      equalBonusCodes
    ) {
      return false;
    }
  }

  return true;
};

export const validateChatSupportDisplayConditions = function (
  this: Yup.TestContext,
  values: {
    [key: string]: chatSupportProviderType;
  }
) {
  if (!values) {
    return false;
  }

  const activeProviders = Object.values(values).filter((el) => el.active);

  if (activeProviders.length < 2) {
    return true;
  }

  const providersDisplayConditions = activeProviders?.map((el) => {
    if (!el?.display_condition) {
      return {};
    }

    return buildDisplayCondition(
      {
        allow: el.display_condition?.allow || displayConditionRuleInitValues,
        deny: el.display_condition?.deny || displayConditionRuleInitValues,
      },
      chatSupportDisplayConditionFieldsConfig
    );
  });

  const hasDisplayConditions = providersDisplayConditions.every(
    (el) => Object.keys(el)?.length > 0
  );

  if (!hasDisplayConditions) {
    return this.createError({
      path: "settings._noConditions",
      message:
        "Please, set up display conditions in order to activate more than one chat provider",
    });
  }

  return true;
};

export const prepareUniqueOptions = (settings, fieldName) => {
  const tempArr = Object.keys(settings).map((lng) =>
    settings?.[lng].map((obj) => obj.conditions?.[fieldName]?.value || [])
  );
  const uniqueElementsArr = [...new Set([...tempArr.flat(2)])];

  return arrToOptionsRaw(uniqueElementsArr);
};
